import { AxiosInstance } from 'axios';
import { Currency } from 'dinero.js';
import type { components, operations } from '@/api/generated/openapi';
import {
    chargeInterface,
    chargeItemInterface,
    ChargeStatus,
    invoiceInterface,
    invoiceStripeAutomaticTaxOptions,
    InvoiceStatus,
    merchantReceivablesInterface,
    storeClientServiceChargeFormInterface,
    storeQuickAdjustmentFormInterface,
} from '@/compiler/billing';
import { DataFetcherFunction, moneyInterface } from '@/compiler/types';
import useFetchAllFromPaginatedResource from '@/composables/useFetchAllFromPaginatedResource';
import useLocalization from '@/composables/useLocalization';
import { axiosWithoutCaseMiddleware, fetchPaginatedDataFunctionWithMapper } from '@/helpers/forms/axios';

export type ChargeRaw = components['schemas']['Charge'];
export type InvoiceRaw = components['schemas']['Invoice'];
export type ChargeItemRaw = components['schemas']['ChargeItem'];

export type ListChargesResponse = operations['list-charges']['responses']['200']['content']['application/json'];
export type ShowChargeResponse = operations['show-charge']['responses']['200']['content']['application/json'];
export type StoreQuickAdjustmentResponse = operations['store-quick-adjustment']['responses']['201']['content']['application/json'];
export type ListInvoicesResponse = operations['list-invoices']['responses']['200']['content']['application/json'];
export type CreateInvoiceResponse = operations['create-invoice']['responses']['200']['content']['application/json'];
export type FinalizeInvoiceResponse = operations['finalize-invoice']['responses']['200']['content']['application/json'];
export type CollectInvoiceResponse = operations['collect-invoice']['responses']['200']['content']['application/json'];
export type CollectInvoiceFailedResponse = operations['collect-invoice']['responses']['400']['content']['application/json'];
export type ListReceivablesResponse = operations['list-receivables']['responses']['200']['content']['application/json'];

export type StoreQuickAdjustmentRequest = operations['store-quick-adjustment']['requestBody']['content']['application/json'];
export type CreateInvoiceRequest = operations['create-invoice']['requestBody']['content']['application/json'];

// The backend has not documented the operations below yet; these hand-written
// types follow their controllers under Conveyr\Ui\Api\Billing.
export interface DeleteChargeResponse {
    message: string
}

export interface PendingChargesTotalPriceResponse {
    amount: number | null
    currency: string | null
}

export interface CurrentBillingCycleEndDateResponse {
    billing_cycle_end_date: string | null
}

export interface StoreClientServiceChargeRequest {
    company_service_id: number
    quantity: number
    description: string
}

export interface StoreClientServiceChargeResponse {
    message: string
    charge: ChargeRaw
    charge_items: ChargeItemRaw[]
}

export interface UpdateInvoiceStripeAutomaticTaxOptionsRequest {
    enabled: boolean | null
}

export interface UpdateInvoiceStripeAutomaticTaxOptionsResponse {
    message: string
    invoice: InvoiceRaw
}

export interface CreateInvoiceOnStripeResponse {
    message: string
}

export interface DeleteInvoiceResponse {
    message: string
}

export interface MarkInvoiceAsPaidResponse {
    message: string
}

export interface VoidInvoiceResponse {
    message: string
}

const chargeStatuses: Record<ChargeRaw['status'], ChargeStatus> = {
    Invoiced: ChargeStatus.Invoiced,
    Voided: ChargeStatus.Voided,
    Open: ChargeStatus.Open,
};

const invoiceStatuses: Record<InvoiceRaw['status'], InvoiceStatus> = {
    Draft: InvoiceStatus.Draft,
    Paid: InvoiceStatus.Paid,
    Finalized: InvoiceStatus.Finalized,
    Voided: InvoiceStatus.Voided,
};

export function toCharge(raw: ChargeRaw): chargeInterface {
    return {
        id: raw.id,
        charger_type: raw.charger_type,
        charger_id: raw.charger_id,
        chargee_type: raw.chargee_type,
        chargee_id: raw.chargee_id,
        created_at: raw.created_at,
        amount: raw.amount,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
        currency: raw.currency as Currency,
        category: raw.category,
        description: raw.description,
        // Never sent by ChargeResource.php; queued for removal from the UI type.
        is_void: undefined,
        is_invoiced: undefined,
        invoice_id: raw.invoice_id,
        stripe_invoice_item_id: raw.stripe_invoice_item_id,
        status: chargeStatuses[raw.status],
    };
}

export function toChargeItem(raw: ChargeItemRaw): chargeItemInterface {
    return {
        id: raw.id,
        charge_id: raw.charge_id,
        parent_id: raw.parent_id,
        chargeable_item_type: raw.chargeable_item_type,
        chargeable_item_id: raw.chargeable_item_id,
        chargeable_item: raw.chargeable_item,
        // Never sent by ChargeItemResource.php; queued for removal from the UI type.
        should_show_units: undefined,
        is_amendment: raw.is_amendment,
        description: raw.description,
        units: raw.units,
        unit_price_amount: raw.unit_price_amount,
        line_price_amount: raw.line_price_amount,
    };
}

export function toInvoice(raw: InvoiceRaw): invoiceInterface {
    return {
        id: raw.id,
        charger_type: raw.charger_type,
        charger_id: raw.charger_id,
        chargee_type: raw.chargee_type,
        chargee_id: raw.chargee_id,
        total: raw.total,
        status: invoiceStatuses[raw.status],
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
        currency: raw.currency as Currency,
        stripe_invoice_id: raw.stripe_invoice_id,
        stripe_invoice_status: raw.stripe_invoice_status,
        created_at: raw.created_at,
        // Never sent by InvoiceResource.php; queued for removal from the UI type.
        voided_at: undefined,
        finalized_at: undefined,
        is_creating_on_stripe: undefined,
        charges: undefined,
        stripe_automatic_tax: raw.stripe_automatic_tax,
    };
}

export function toReceivables(raw: ListReceivablesResponse[number]): merchantReceivablesInterface {
    return {
        merchant_id: raw.merchant_id,
        open_charges_count: raw.open_charges_count,
        draft_invoices_count: raw.draft_invoices_count,
        unpaid_invoices_count: raw.unpaid_invoices_count,
        open_charges_amount: Number.parseFloat(raw.open_charges_amount),
        draft_invoices_amount: Number.parseFloat(raw.draft_invoices_amount),
        unpaid_invoices_amount: Number.parseFloat(raw.unpaid_invoices_amount),
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves billing_currency a free string, not an ISO 4217 code
        currency: raw.billing_currency as Currency,
    };
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): { indexCharges: () => DataFetcherFunction<chargeInterface>; showCharge: (chargeId: number) => Promise<[chargeInterface, Array<chargeItemInterface & Record<'chargeable_item', unknown>>]>; deleteCharge: (chargeId: number) => Promise<boolean>; getPendingChargesTotalPrice: () => Promise<moneyInterface>; getCurrentBillingCycleEnd: () => Promise<string | null>; storeQuickAdjustment: (form: storeQuickAdjustmentFormInterface) => Promise<boolean>; storeClientServiceCharge: (form: storeClientServiceChargeFormInterface) => Promise<boolean>; indexInvoices: () => DataFetcherFunction<invoiceInterface>; createInvoice: (chargeIds: CreateInvoiceRequest['charge_ids']) => Promise<invoiceInterface>; finalizeInvoice: (invoiceId: number) => Promise<void>; updateStripeAutomaticTaxOptions: (invoice_id: number, options: invoiceStripeAutomaticTaxOptions) => Promise<invoiceInterface>; createOnStripe: (invoiceId: number) => Promise<void>; geDownloadInvoiceRedirectUrl: (invoiceId: number) => string; collectInvoice: (invoiceId: number) => Promise<void>; deleteInvoice: (invoiceId: number) => Promise<void>; markInvoiceAsPaid: (invoiceId: number) => Promise<void>; voidInvoice: (invoiceId: number) => Promise<void>; getReceivables: () => Promise<merchantReceivablesInterface[]>; getChargesForChargeable: (entityType: string, entityId: string) => Promise<chargeInterface[]>; } => ({
    indexCharges: (): DataFetcherFunction<chargeInterface> => {
        return fetchPaginatedDataFunctionWithMapper<ListChargesResponse['data'][number], chargeInterface>('/api/billing/charges', toCharge, undefined, axios);
    },

    showCharge: async (chargeId: number): Promise<[chargeInterface, Array<chargeItemInterface & Record<'chargeable_item', unknown>>]> => {
        const response = await axios.get<ShowChargeResponse>(`/api/billing/charges/${chargeId}`);

        return [
            toCharge(response.data.charge),
            response.data.charge_items.map(toChargeItem),
        ];
    },

    deleteCharge: async (chargeId: number): Promise<boolean> => {
        await axios.delete<DeleteChargeResponse>(`/api/billing/charges/${chargeId}`);
        return true;
    },

    getPendingChargesTotalPrice: async (): Promise<moneyInterface> => {
        const response = await axios.get<PendingChargesTotalPriceResponse>('/api/billing/charges/pending-total-price');

        const { displayCurrency } = useLocalization();

        return {
            amount: response.data.amount ?? 0,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the backend sends an ISO 4217 code, but this undocumented operation is typed with a free string
            currency: (response.data.currency as Currency | null) ?? displayCurrency.value,
        };
    },

    getCurrentBillingCycleEnd: async (): Promise<string | null> => {
        const response = await axios.get<CurrentBillingCycleEndDateResponse>('/api/billing/charges/current-billing-cycle-end-date');

        return response.data.billing_cycle_end_date;
    },

    storeQuickAdjustment: async (form: storeQuickAdjustmentFormInterface): Promise<boolean> => {
        const body: StoreQuickAdjustmentRequest = {
            amount: form.amount,
            description: form.description,
        };
        await axios.post<StoreQuickAdjustmentResponse>('/api/billing/charges/quick-adjustment', body);

        return true;
    },

    storeClientServiceCharge: async (form: storeClientServiceChargeFormInterface): Promise<boolean> => {
        const body: StoreClientServiceChargeRequest = {
            company_service_id: form.company_service_id,
            quantity: form.quantity,
            description: form.description,
        };
        await axios.post<StoreClientServiceChargeResponse>('/api/billing/charges/client-service-charge', body);

        return true;
    },

    indexInvoices: (): DataFetcherFunction<invoiceInterface> => {
        return fetchPaginatedDataFunctionWithMapper<ListInvoicesResponse['data'][number], invoiceInterface>('/api/billing/invoices', toInvoice, undefined, axios);
    },

    createInvoice: async (chargeIds: CreateInvoiceRequest['charge_ids']): Promise<invoiceInterface> => {
        const body: CreateInvoiceRequest = { charge_ids: chargeIds };
        const response = await axios.post<CreateInvoiceResponse>('/api/billing/invoices', body);

        return toInvoice(response.data.invoice);
    },

    finalizeInvoice: async (invoiceId: number): Promise<void> => {
        await axios.post<FinalizeInvoiceResponse>(`/api/billing/invoices/${invoiceId}/finalize`);
    },

    updateStripeAutomaticTaxOptions: async (invoice_id: number, options: invoiceStripeAutomaticTaxOptions): Promise<invoiceInterface> => {
        const body: UpdateInvoiceStripeAutomaticTaxOptionsRequest = options;
        const response = await axios.put<UpdateInvoiceStripeAutomaticTaxOptionsResponse>(`/api/billing/invoices/${invoice_id}/stripe-automatic-tax-options`, body);

        return toInvoice(response.data.invoice);
    },

    createOnStripe: async (invoiceId: number): Promise<void> => {
        await axios.post<CreateInvoiceOnStripeResponse>(`/api/billing/invoices/${invoiceId}/create-on-stripe`);
    },

    geDownloadInvoiceRedirectUrl: (invoiceId: number): string => {
        return `/api/billing/invoices/${invoiceId}/download/pdf`;
    },

    collectInvoice: async (invoiceId: number): Promise<void> => {
        await axios.post<CollectInvoiceResponse>(`/api/billing/invoices/${invoiceId}/collect`);
    },

    deleteInvoice: async (invoiceId: number): Promise<void> => {
        await axios.delete<DeleteInvoiceResponse>(`/api/billing/invoices/${invoiceId}`);
    },

    markInvoiceAsPaid: async (invoiceId: number): Promise<void> => {
        await axios.post<MarkInvoiceAsPaidResponse>(`/api/billing/invoices/${invoiceId}/mark-as-paid`);
    },

    voidInvoice: async (invoiceId: number): Promise<void> => {
        await axios.post<VoidInvoiceResponse>(`/api/billing/invoices/${invoiceId}/void`);
    },

    getReceivables: async (): Promise<merchantReceivablesInterface[]> => {
        const response = await axios.get<ListReceivablesResponse>('/api/billing/receivables');

        return response.data.map(toReceivables);
    },

    getChargesForChargeable: async (entityType: string, entityId: string): Promise<chargeInterface[]> => {
        const fetcher = fetchPaginatedDataFunctionWithMapper<ListChargesResponse['data'][number], chargeInterface>('/api/billing/charges', toCharge, undefined, axios);

        return useFetchAllFromPaginatedResource<chargeInterface>(fetcher, {
            pageSize: 100,
            filters: `items.chargeable_item_type:"${entityType}" AND items.chargeable_item_id:"${entityId}"`,
        })();
    },
});
